import { useEffect, useMemo, useRef } from "react";

/** Keep one user action active and ignore async callbacks after leaving its screen. */
export function useHostedStepAction(identity = "step") {
  const action = useRef({ mounted: true, busy: false, completed: false, generation: 0 });

  useEffect(() => {
    const current = action.current;
    current.mounted = true;
    current.busy = false;
    current.completed = false;
    return () => {
      current.mounted = false;
      current.generation += 1;
    };
  }, [identity]);

  return useMemo(() => {
    const isCurrent = (generation: number) =>
      action.current.mounted && action.current.generation === generation;
    return {
      begin: () => {
        if (!action.current.mounted || action.current.busy || action.current.completed) return;
        action.current.busy = true;
        return action.current.generation;
      },
      isCurrent,
      complete: (generation: number) => {
        if (!isCurrent(generation) || action.current.completed) return false;
        action.current.completed = true;
        return true;
      },
      finish: (generation: number) => {
        if (isCurrent(generation)) action.current.busy = false;
      },
    };
  }, []);
}
